import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import { financialCategorySchema, financialCategoryDefaults } from "../../../../models/financialCategory";
import { 
    NeoCard, 
    NeoButton, 
    NeoInput, 
    NeoBadge, 
    NeoEmptyState, 
    OverlayLoader 
} from "../../../../components/Common";
import { FINANCIAL_TYPES } from "../../../../constants/financial";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";

/**
 * FinancialCategoryEditor
 * Editor visual para Categorias Financeiras (Plano de Contas / DRE).
 * Migrado para o Neo Design System (100% Neo components e zero estilos inline).
 */
const FinancialCategoryEditor = ({ category, onSave, onDelete, isSaving, onChange }) => {

    const formik = useFormik({
        initialValues: financialCategoryDefaults,
        validationSchema: financialCategorySchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    // Sincroniza estado quando a categoria selecionada muda
    useEffect(() => {
        if (category) {
            formik.resetForm({
                values: {
                    id: category.id || "",
                    name: category.name || "",
                    type: category.type || FINANCIAL_TYPES.INCOME,
                    parentId: category.parentId || null,
                    parentName: category.parentName || "",
                    order: category.order || 0,
                    isSystem: !!category.isSystem,
                    status: category.status || GLOBAL_STATUS.ACTIVE,
                    createdAt: category.createdAt || null,
                    createdBy: category.createdBy || null
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category]);

    // Notifica mudança para o pai (isDirty)
    useEffect(() => {
        onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    // Estado vazio
    if (!category) {
        return (
            <NeoEmptyState
                icon="mdi-tag-multiple-outline"
                title="Selecione uma categoria"
                description="Escolha uma categoria na lista ao lado ou crie uma nova para classificar seus lançamentos financeiros."
            />
        );
    }

    const isSystemCategory = formik.values.isSystem;

    return (
        <NeoCard
            title={formik.values.id ? formik.values.name : "Nova Categoria"}
            subtitle={
                <span className="d-flex align-items-center gap-2 mt-1">
                    <span>
                        {formik.values.type === FINANCIAL_TYPES.INCOME ? "Categoria de Receita" : "Categoria de Despesa"}
                    </span>
                    {formik.values.id && (
                        <NeoBadge color={formik.values.status === GLOBAL_STATUS.ACTIVE ? "success" : "danger"}>
                            {GLOBAL_STATUS_LABELS[formik.values.status] || "Desconhecido"}
                        </NeoBadge>
                    )}
                </span>
            }
            headerIcon="mdi-tag-multiple-outline"
            headerActions={
                <div className="d-flex gap-2">
                    {formik.values.id && !isSystemCategory && (
                        <NeoButton
                            variant="outline-danger"
                            square={true}
                            size="md"
                            icon="mdi-trash-can-outline"
                            onClick={() => onDelete(formik.values.id)}
                            disabled={isSaving}
                            title="Excluir Categoria"
                        />
                    )}
                    <NeoButton
                        variant="primary"
                        loading={isSaving}
                        loadingText="Salvando..."
                        onClick={formik.handleSubmit}
                        icon="mdi-check"
                    >
                        Salvar
                    </NeoButton>
                </div>
            }
        >
            <OverlayLoader show={isSaving} label="Salvando..." />
            
            <div className="row">
                <div className="col-12">
                    <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                        <i className="mdi mdi-information-outline me-2 font-size-16 text-primary"></i>
                        Dados da Categoria
                    </h6>
                </div>

                {/* Nome */}
                <div className="col-md-6 mb-3">
                    <NeoInput
                        label="Nome da Categoria *"
                        name="name"
                        type="text"
                        placeholder="Ex: Mensalidades, Aluguel, Salários..."
                        {...formik.getFieldProps("name")}
                        error={formik.touched.name && formik.errors.name}
                    />
                </div>

                {/* Tipo */}
                <div className="col-md-3 mb-3">
                    <NeoInput
                        label="Tipo *"
                        name="type"
                        type="select"
                        {...formik.getFieldProps("type")}
                        error={formik.touched.type && formik.errors.type}
                        disabled={isSystemCategory}
                    >
                        <option value={FINANCIAL_TYPES.INCOME}>💰 Receita</option>
                        <option value={FINANCIAL_TYPES.EXPENSE}>📉 Despesa</option>
                    </NeoInput>
                </div>

                {/* Status */}
                <div className="col-md-3 mb-3 d-flex flex-column justify-content-end pb-1">
                    <label className="neo-input-label mb-2">Status</label>
                    <div className="form-check form-switch form-switch-md">
                        <input
                            type="checkbox"
                            className="form-check-input custom-switch-primary cursor-pointer"
                            checked={formik.values.status === GLOBAL_STATUS.ACTIVE}
                            onChange={(e) => {
                                formik.setFieldValue("status", e.target.checked ? GLOBAL_STATUS.ACTIVE : GLOBAL_STATUS.INACTIVE);
                                onChange(true);
                            }}
                            id="statusSwitch"
                        />
                        <label className="form-check-label ms-2 small text-muted cursor-pointer" htmlFor="statusSwitch">
                            {GLOBAL_STATUS_LABELS[formik.values.status] || formik.values.status}
                        </label>
                    </div>
                </div>

                {/* Ordem */}
                <div className="col-md-3 mb-3">
                    <NeoInput
                        label="Ordem na DRE"
                        name="order"
                        type="number"
                        placeholder="0"
                        {...formik.getFieldProps("order")}
                    />
                    <small className="text-muted d-block mt-1">Define a posição no relatório DRE</small>
                </div>
            </div>
        </NeoCard>
    );
};

FinancialCategoryEditor.propTypes = {
    category: PropTypes.object,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default FinancialCategoryEditor;
